import { css, html, LitEntity, SirenActionMixin } from '../../utility/lit-entity';
import '../../common/bn-loading';
import '../../common/bn-action';
import '../../common/bn-load-more-button';
import '../../common/bn-surface';
import '../../organizations/parts/organization-ancestors-bread-crumbs';
import '../../organizations/parts/organization-bread-crumbs';
import '../../organizations/parts/organization-dropdown';
import '../actions/bin-move-action';
import { sharedStyles } from '../../styles/shared-styles';

class BinMove extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.query = '';
    this.searchResultOrganizations = [];
    this.childOrganizations = [];
  }

  static get properties() {
    return {
      currentbinownerid: { type: String },
      query: { type: String, attribute: false },
      isFetching: { type: Boolean, attribute: false },
      searchResultOrganizations: { type: Array, attribute: false },
      selectedOrganization: { type: Object, attribute: false },
      childOrganizations: { type: Array, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .actions {
        margin-top: 2em;
        padding: 10px;
        display: flex;
        align-items: center;
        font-size: 1.1em;
      }
      .actions .move-to {
        padding-left: 1em;
      }
      .actions .move-to .destination-name {
        font-weight: bold;
      }
      .main-container {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      .search-container {
          margin-left: auto;
          margin-right: 0.5em;
          width: 70%;
      }
      organization-ancestors-bread-crumbs, organization-bread-crumbs {
        display: inline-block;
        flex-grow: 1;
        --crumb-max-width: 100%;
        --last-crumb-overflow: visible;
        --last-crumb-font-size: 1.1em;
        --last-crumb-font-weight: bold;
      }
      .destination-container {
        margin-top: 20px;
      }
      .destination-header {
        display: flex;
        align-items: baseline;
      }
      .search-results {
        overflow-y: auto;
        margin-top: 0.5em;
      }
      .search-result-item {
        display: flex;
        align-items: center;
        cursor: pointer;
      }
      .search-result-item:hover {
        background-color: var(--hover-color);
      }
      input[type="search"] {
        text-indent: 25px;
        border-radius: 4px;
        background-color: var(--white-base);
      }
      .search-icon {
        position: absolute;
        transform: translate(7px, 5px);
      }
      .empty-search-results {
        padding-left: 1em;
      }
    `];
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.searchResultOrganizations = this.entity.getSubEntitiesByRel('item');
    this.selectedOrganization = this.entity.getSubEntityByRel('https://api.binsentry.com/rel/selected-organization');
    this.childOrganizations = this.entity.getSubEntitiesByRel('https://api.binsentry.com/rel/organization-child');
    this.ancestorOrganizations = this.entity.getSubEntitiesByRel('https://api.binsentry.com/rel/organization-ancestor');
    const flash = this.shadowRoot.querySelector('#status');
    flash && flash.clear();
  }

  _searchQuery() {
    return new URL(this.href).searchParams.get('searchQuery') || '';
  }

  render() {
    this._adjustModalDialogHeight();

    if (!this.entity) {
      return html`<bn-loading></bn-loading>`;
    }

    return html`
      <div class="main-container">
        <bn-flash id="status"></bn-flash>
        ${this._renderInfoText()}
        ${this._renderCurrentLocation()}
        ${this._renderDestinationPicker()}
        ${this._renderContents()}
      </div>
     `;
  }

  _adjustModalDialogHeight() {
    const binMoveModal = document.querySelector('bn-modal#bin-move-modal');
    if (this._searchQuery()) {
      binMoveModal.setAttribute('noscroll', true);
      binMoveModal.style.setProperty('--modal-min-height', '95%');
      return;
    }
    binMoveModal.removeAttribute('noscroll');
    binMoveModal.style.setProperty('--modal-min-height', '400px');
  }

  _renderInfoText() {
    return html`
      <div class="subdued">
        <p>
          Choose the destination organization for the bin, and then click the move button to move
          it to the target organization.
        </p>
      </div>
    `;
  }

  _renderCurrentLocation() {
    return html`
      <h4>Current location:</h4>
      <bn-surface>
        <organization-ancestors-bread-crumbs
          .href="${this.currentbinownerid}"
          ancestorMinIndex="0"
        ></organization-ancestors-bread-crumbs>
      </bn-surface>
    `;
  }

  _renderDestinationPicker() {
    return html`
      <div class="destination-container">
        <div class="destination-header">
          <h4>Destination:</h4>
          ${this._renderSearchBox()}
        </div>
      </div>
    `;
  }

  _renderSearchBox() {
    return html`
      <div class="search-container">
        <form @submit="${this._submitSearch}">
          <span>
            <bn-icon name="fal fa-search" class="search-icon"></bn-icon>
            <input
              type="search"
              placeholder="Search organizations"
              .value="${this.query}"
              @input="${this._handleSearchQueryChange}"
            />
          </span>
        </form>
      </div>
    `;
  }

  async _submitSearch(e) {
    e && e.preventDefault();
    const action = this.entity.getActionByName('search-organizations');

    const fields = this.getActionFields(action);
    fields.set('searchQuery', this.query);

    const url = new URL(action.href);
    url.search = fields;

    this.href = url.href;
  }

  _handleSearchQueryChange(e) {
    this.query = e.target.value;
    // automatically switch back to browse when search box is cleared
    if (!this.query && this._searchQuery()) {
      this._submitSearch();
    }
  }

  _renderContents() {
    if (this.loading) {
      return html`<bn-loading></bn-loading>`;
    }
    if (this._searchQuery()) {
      return this._renderSearchResults();
    }

    return this._renderOrgBrowser();
  }

  _renderSearchResults() {
    if (this.searchResultOrganizations.length === 0) {
      return html`<p class="empty-search-results">No organizations found</p>`;
    }
    return html`
      <div class="search-results">
        ${this.searchResultOrganizations.map((organization) => html`
          <bn-surface class="search-result-item" @click="${() => this._selectOrganization(organization)}">
            <organization-ancestors-bread-crumbs
              .href="${organization.getLinkByRel('via').href}"
            ></organization-ancestors-bread-crumbs>
          </bn-surface>
        `)}
        <bn-load-more-button
          .href="${this.href}"
          @moreloaded="${this._onMoreLoaded}"
        ></bn-load-more-button>
      </div>
    `;
  }

  _onMoreLoaded(e) {
    const { entity } = e.detail;
    this.searchResultOrganizations = [...this.searchResultOrganizations, ...entity.getSubEntitiesByRel('item')];
  }

  _renderOrgBrowser() {
    if (!this.selectedOrganization) {
      return html``;
    }

    const crumbItems = [
      ...this.ancestorOrganizations.map((organization) => ({
        organization,
        onClick: () => this._selectOrganization(organization),
      })),
      { organization: this.selectedOrganization },
    ];

    return html`
      <bn-surface>
        <organization-bread-crumbs .organizationItems="${crumbItems}" explicitlinks>
          <organization-dropdown
            .organizations="${this.childOrganizations}"
            @organizationclicked="${(e) => this._selectOrganization(e.detail.organization)}"
          ></organization-dropdown>
        </organization-bread-crumbs>
      </bn-surface>

      ${this._renderActionWell()}
    `;
  }

  _selectOrganization(org) {
    this.query = '';
    this.href = org.getActionByName('select').href;
  }

  _renderActionWell() {
    return html`
      <div class="actions well">
        <bin-move-action .href="${this.href}" @error="${this._onError}"></bin-move-action>
        <span class="move-to">
          to <span class="destination-name">${this.selectedOrganization.properties.name}</span>
        </span>
      </div>
    `;
  }

  _onError(e) {
    const flash = this.shadowRoot.querySelector('#status');
    flash.clear();

    const { error } = e;
    flash.critical(html`Error<br/><code>${error.message}</code>`);
  }
}

window.customElements.define('bin-move', BinMove);
